import React from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';

import { withValue } from '@g4/automation-hooks';

import {
  Expando,
  Text,
  Box,
  VerticalSpacing,
  useMedia,
} from '../../../Boneyard';
import Markdown from '../../../Markdown';
import { getPrice } from '@g4/next-furniture/Price/Price';
import PriceBreakdownItem from './flightPackagePriceBreakdown/PriceBreakdownItem';
import { useTranslations } from '../../../../graphql/components/TranslationsProvider';
import { PRICE_BREAKDOWN } from '../../../../../automationHooks.json';
import ARROW_ICON from '../../../../images/ic-arrow-blue.svg';

const feesLabels = {
  CCV: 'carrier-usage-charge',
  ZP: 'segment-fees',
  XT: 'fed-tax',
  PFC: 'pfc',
  SEC: '911-security',
  US1: 'US Transportation',
};

const propTypes = {
  destination: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired,
  airlineFeesItems: PropTypes.array.isRequired,
  airlineFeesTotal: PropTypes.number.isRequired,
  governmentFeesItems: PropTypes.array.isRequired,
  governmentFeesTotal: PropTypes.number.isRequired,
  flightsDiscountAmount: PropTypes.number.isRequired,
  isRoundTripDiscountType: PropTypes.bool,
  isTripSummary: PropTypes.bool,
  flightHotelCarLabel: PropTypes.string,
  flightHotelCarTotal: PropTypes.number.isRequired,
  hidePrice: PropTypes.bool,
  isStadium: PropTypes.bool,
  bogoFlightDiscountAmount: PropTypes.number.isRequired,
  isBOGOApplied: PropTypes.bool,
};

const PriceList = styled.ul`
  font-size: ${props => props.fontSize}px;
  margin-bottom: ${props => props.theme.spacing(2)};

  & & {
    padding-left: ${props => props.theme.spacing(6)};
  }
`;

const PriceListItem = styled.li`
  margin-bottom: ${props => props.theme.spacing(2)};
`;

const ArrowIcon = styled.img.attrs({
  src: ARROW_ICON,
  alt: '',
})`
  width: 17px;
  height: 17px;
`;

const ArrowIconContainer = styled.div`
  margin-right: ${props => props.theme.spacing(1)};
  position: absolute;
  right: 0;

  transform: ${props =>
    props.isOpen
      ? 'rotate(0deg) translateY(0)'
      : 'rotate(180deg) translateY(2px)'};
  transition: 0.2s;
`;

const FlightPackagePriceBreakdown = ({
  destination,
  theme,
  airlineFeesItems,
  airlineFeesTotal,
  governmentFeesItems,
  governmentFeesTotal,
  flightsDiscountAmount,
  isRoundTripDiscountType,
  isTripSummary,
  flightHotelCarLabel,
  flightHotelCarTotal,
  hidePrice,
  isStadium,
  bogoFlightDiscountAmount,
  isBOGOApplied,
}) => {
  const mq = useMedia();
  const translations = useTranslations();

  const {
    roundTripDiscountAppliedLabel,
    roundTripDiscountAppliedModalContent,
    generalFlightDiscountAppliedLabel,
    governmentFeesLabel,
    airlineFeesLabel,
    bogoDiscountLabel,
    bogoDiscountModalContentHtml,
    promoAppliedLabel,
  } = isTripSummary ? translations.paymentPage : translations.cart;

  // Airline Fees Group
  const [isAirlineFeesExpanded, setIsAirlineFeesExpanded] = React.useState(
    isTripSummary
  );

  // Government Fees Group
  const [
    isGovernmentFeesExpanded,
    setIsGovernmentFeesExpanded,
  ] = React.useState(isTripSummary);

  const negativeFlightsDiscountAmount = flightsDiscountAmount * -1;
  const { price: roundTripPriceString } = getPrice(flightsDiscountAmount, {
    hideEmptyCents: true,
  });
  const { price: bogoDiscountPrice } = getPrice(bogoFlightDiscountAmount, { hideEmptyCents: true })

  const addFlightDiscountPopoverProps = () => {
    let name = generalFlightDiscountAppliedLabel;
    let popoverContent = null;
  
    if (isRoundTripDiscountType) {
      name = roundTripDiscountAppliedLabel;
      popoverContent = (
        <Markdown
          source={roundTripDiscountAppliedModalContent}
          data={{ amount: roundTripPriceString }}
        />
      );
    } else if (isBOGOApplied) {
      name = bogoDiscountLabel;
      popoverContent = (
        <Markdown
          source={bogoDiscountModalContentHtml}
          data={{ amount: bogoDiscountPrice }}
        />
      );
    }
  
    return { name, popoverContent };
  };

  const flightDiscountPopoverProps = addFlightDiscountPopoverProps();
  const hasFlightDiscount = isBOGOApplied || (flightsDiscountAmount > 0);

  return (
    <Box
      h="100%"
      position="relative"
      bl={
        isTripSummary
          ? '1px solid' + theme.colors.cart.priceBreakdown.item.border
          : 'none'
      }
    >
      <Text size={mq.lte('sm') ? 13 : 15} weight={500}>
      {hasFlightDiscount && (
          <Box
            bb={'1px solid' + theme.colors.cart.priceBreakdown.item.border}
            pr={isTripSummary ? (mq.lte('md') ? 3 : 6) : 6}
            pl={isTripSummary ? 4 : 6}
            pt={2}
            backgroundColor={theme.colors.cart.item.highlightBackground}
          >
            <PriceBreakdownItem
              amount={negativeFlightsDiscountAmount}
              destination={destination}
              automationHook={withValue(PRICE_BREAKDOWN.hook, 'flight-discount')}
              {...flightDiscountPopoverProps}
              hidePrice={hidePrice}
              isPromoApplied={isBOGOApplied}
              promoAppliedLabel={promoAppliedLabel}
            />
            <VerticalSpacing size={2} />
          </Box>
        )}
        {isTripSummary && (
          <Box
            pl={4}
            pr={isTripSummary ? 3 : 6}
            bb={
              !isStadium &&
              '2px solid' + theme.colors.cart.priceBreakdown.item.border
            }
          >
            <Box>
              <VerticalSpacing size={2} />
              {!isStadium && (
                <PriceBreakdownItem
                  name={flightHotelCarLabel}
                  amount={flightHotelCarTotal}
                  automationHook={withValue(PRICE_BREAKDOWN.hook, 'subtotal')}
                />
              )}
              <VerticalSpacing size={2} />
            </Box>
          </Box>
        )}
        {(governmentFeesTotal > 0 || airlineFeesTotal > 0) && (
          <Box pl={6} pr={isTripSummary ? (mq.lte('md') ? 3 : 6) : 6}>
            <VerticalSpacing size={2} />

            <PriceList>
              {airlineFeesTotal > 0 && (
                <PriceListItem>
                  <Expando
                    label={
                      <>
                        <PriceBreakdownItem
                          name={airlineFeesLabel}
                          amount={airlineFeesTotal}
                          automationHook={withValue(
                            PRICE_BREAKDOWN.hook,
                            'airline-fees'
                          )}
                          hidePrice={hidePrice}
                        />
                        <ArrowIconContainer isOpen={isAirlineFeesExpanded}>
                          {!isTripSummary && <ArrowIcon />}
                        </ArrowIconContainer>
                      </>
                    }
                    open={isAirlineFeesExpanded}
                    onToggle={() => {
                      if (isTripSummary) {
                        return;
                      }
                      setIsAirlineFeesExpanded(!isAirlineFeesExpanded);
                    }}
                    icon="NONE"
                    a11yLabel="Toggle Airline Fees"
                  >
                    {isAirlineFeesExpanded && (
                      <>
                        <VerticalSpacing size={2} />

                        <PriceList fontSize={mq.lte('sm') ? 12 : 13}>
                          {airlineFeesItems.map((item, index) => (
                            <PriceListItem key={index}>
                              <PriceBreakdownItem
                                name={item.name}
                                amount={item.amount}
                                amountStyles={{
                                  fontWeight: 'normal',
                                  fontSize: mq.lte('sm') ? 13 : 14,
                                }}
                                automationHook={withValue(
                                  PRICE_BREAKDOWN.hook,
                                  feesLabels[item.code]
                                )}
                                hidePrice={hidePrice}
                              />
                            </PriceListItem>
                          ))}
                        </PriceList>
                      </>
                    )}
                  </Expando>
                </PriceListItem>
              )}

              {governmentFeesTotal > 0 && (
                <PriceListItem>
                  <Expando
                    label={
                      <>
                        <PriceBreakdownItem
                          name={governmentFeesLabel}
                          amount={governmentFeesTotal}
                          automationHook={withValue(
                            PRICE_BREAKDOWN.hook,
                            'government-taxes-fees'
                          )}
                          hidePrice={hidePrice}
                        />
                        <ArrowIconContainer isOpen={isGovernmentFeesExpanded}>
                          {!isTripSummary && <ArrowIcon />}
                        </ArrowIconContainer>
                      </>
                    }
                    open={isGovernmentFeesExpanded}
                    onToggle={() => {
                      if (isTripSummary) {
                        return;
                      }
                      setIsGovernmentFeesExpanded(!isGovernmentFeesExpanded);
                    }}
                    icon="NONE"
                    a11yLabel="Toggle Government Taxes and Fees"
                  >
                    {isGovernmentFeesExpanded && (
                      <>
                        <VerticalSpacing size={2} />

                        <PriceList fontSize={mq.lte('sm') ? 12 : 13}>
                          {governmentFeesItems.map((item, index) => (
                            <PriceListItem key={index}>
                              <PriceBreakdownItem
                                name={item.name}
                                amount={item.amount}
                                amountStyles={{
                                  fontWeight: 'normal',
                                  fontSize: mq.lte('sm') ? 13 : 14,
                                }}
                                automationHook={withValue(
                                  PRICE_BREAKDOWN.hook,
                                  feesLabels[item.code]
                                )}
                                hidePrice={hidePrice}
                              />
                            </PriceListItem>
                          ))}
                        </PriceList>
                      </>
                    )}
                  </Expando>
                </PriceListItem>
              )}
            </PriceList>
          </Box>
        )}
      </Text>
    </Box>
  );
};

FlightPackagePriceBreakdown.propTypes = propTypes;

export default withTheme(FlightPackagePriceBreakdown);
